import { useMemo } from "react";
import { useAccessManagement } from "@visioni/a3s-access/app";

/**
 * Returns role flags for a given user ID.
 *
 * @param userId - The user ID to check roles for.
 * @param siteId - Optional site ID to check site-specific roles (isManager, isViewer).
 *
 * @example
 * // Check if a specific user is tenant admin
 * const { isTenantAdmin } = useUserRoles(userId);
 *
 * // Check if a user is manager on a specific site
 * const { isManager, isViewer } = useUserRoles(userId, siteId);
 */
export function useUserRoles(userId: string | undefined, siteId?: string) {
  const { data } = useAccessManagement();

  return useMemo(() => {
    if (!data || !userId) {
      return { isTenantAdmin: false, isManager: false, isViewer: false };
    }

    const userRoles = data.roles.filter((r) => String(r.user) === userId);

    const isTenantAdmin = userRoles.some((r) => r.role === "tenant_admin");

    const isManager =
      siteId ?
        userRoles.some((r) => r.role === `site_manager:${siteId}`)
      : userRoles.some((r) => r.role.startsWith("site_manager:"));

    const isViewer =
      siteId ?
        userRoles.some((r) => r.role === `site_user:${siteId}`)
      : userRoles.some((r) => r.role.startsWith("site_user:"));

    return { isTenantAdmin, isManager, isViewer };
  }, [data, userId, siteId]);
}
